import { AlertTriangle, CheckCircle2, ChevronRight, Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { EnterpriseDetailRecord } from '@/components/enterprise/EnterpriseRecordDetailSheet';
import type { PartnerCampaignOverviewRow } from '@/hooks/useEnterpriseEmailMonitoring';

interface CampaignOverviewPanelProps {
  campaigns: PartnerCampaignOverviewRow[];
  partnerId: string;
  /** Open a campaign's detail in the inline Sheet (no workspace switch). */
  onSelect: (record: EnterpriseDetailRecord) => void;
}

/** Render a 0..1 rate as a percentage string (e.g. 0.1234 → "12.3%"). */
function formatRate(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

/**
 * M2 (#572): cross-workspace campaign overview for an enterprise partner.
 *
 * Reuses the SafetyAlertsPanel status vocabulary (red = needs-attention) and the
 * shadcn Card/Badge/Table idiom. Rows flagged `needs_attention` get a red badge
 * and a "View" action that opens the campaign's detail in an inline Sheet.
 */
export function CampaignOverviewPanel({
  campaigns,
  partnerId,
  onSelect,
}: CampaignOverviewPanelProps): JSX.Element {
  return (
    <Card data-partner-id={partnerId}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Campaigns
        </CardTitle>
      </CardHeader>
      <CardContent>
        {campaigns.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-green-500" />
            <div>No campaigns</div>
            <div className="text-sm">
              No campaigns have been created across these workspaces yet.
            </div>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Workspace</TableHead>
                <TableHead>Campaign</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Sent / Failed / Pending</TableHead>
                <TableHead className="text-right">Failure %</TableHead>
                <TableHead className="text-right">Unsub %</TableHead>
                <TableHead className="text-right">Attention</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {campaigns.map((c) => (
                <TableRow key={c.campaign_id}>
                  <TableCell className="text-sm text-muted-foreground">
                    {c.workspace_name}
                  </TableCell>
                  <TableCell className="text-sm font-medium">{c.campaign_name}</TableCell>
                  <TableCell className="text-sm">
                    <Badge variant="outline">{c.status}</Badge>
                  </TableCell>
                  <TableCell className="text-right text-sm tabular-nums">
                    {c.emails_sent} / {c.emails_failed} / {c.emails_pending}
                  </TableCell>
                  <TableCell className="text-right text-sm tabular-nums">
                    {formatRate(c.failure_rate)}
                  </TableCell>
                  <TableCell className="text-right text-sm tabular-nums">
                    {formatRate(c.unsubscribe_rate)}
                  </TableCell>
                  <TableCell className="text-right">
                    {c.needs_attention ? (
                      <div className="flex items-center justify-end gap-2">
                        <Badge variant="destructive" className="gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          Needs attention
                        </Badge>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onSelect({ type: 'campaign', data: c })}
                        >
                          View
                          <ChevronRight className="ml-1.5 h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ) : (
                      <Badge variant="secondary" className="bg-green-100 text-green-700">
                        OK
                      </Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
